// What the lab report inbox says, in words (TGCLOUD-1866, Phase 3). Plan:
// claude-MDs/PLAN-tgcloud-1866-structured-test-results.md, "Human workflow" and "Matching".
//
// THE CODES ARE THE BACKEND'S. Every list below is a copy of backend/services/testResults/rules.js
// (or the matcher's own constants), and labReportInboxVocabulary.test.js reads the backend and fails
// when they part: a reason the dialog offers that the server refuses is a decision nobody can make,
// and one the server accepts that the screen cannot name is a log entry nobody can read.
//
// THE BANDS ARE RULES, NOT SCORES. A band says which fixed rule held (inboxMatcher.bandFor), so it is
// shown as its name and the rule in a sentence, never as a percentage, a bar or a colour scale that
// reads as one: a number invites the automation bias the plan's hazard log names.

import { translate } from 'src/hooks/useTranslate';

const freeze = (values) => Object.freeze([...values]);

export const INBOX_STATES = freeze(['open', 'matched', 'linked', 'dismissed']);

export const STATE_LABELS = Object.freeze({
  open: 'Open',
  matched: 'Matched',
  linked: 'Linked',
  dismissed: 'Dismissed',
  // One person of a report about several, never a whole report's (TGCLOUD-1868, decision 28).
  'set-aside': 'Set aside'
});

// One line under each tab, so the four states read as the workflow they are.
export const STATE_DESCRIPTIONS = Object.freeze({
  open: 'Reports waiting for someone to confirm which patient they are about.',
  matched:
    'Reports matched to a patient, waiting to be applied to one of their Tests.',
  linked:
    'Reports applied to a Test. The Test record is where the result now lives.',
  // A report about several people is also dismissed with everyone on it set aside (TGCLOUD-1868,
  // decision 28), and comes back by undoing one of them.
  dismissed:
    'Reports dismissed with a reason, and reports whose people are all set aside as not our patient. A dismissed report can be reopened, and a set-aside undone.'
});

export const INBOX_SOURCES = freeze([
  'upload',
  'feed',
  'test-upload',
  'returned'
]);

export const SOURCE_LABELS = Object.freeze({
  upload: 'Uploaded to the inbox',
  feed: 'Received from a feed',
  'test-upload': 'Sent from a Test',
  returned: 'Returned from a Test'
});

export const INBOX_REASONS = freeze([
  'AWAITING_MATCH',
  'AMBIGUOUS',
  'UNREADABLE',
  'CONFLICT',
  // Flagged on arrival (TGCLOUD-1868, the Phase E reconciliation): the report gives results for more
  // than one person, or for a pregnancy, and the reader could not tell whose each result is.
  'PEOPLE_NOT_SPLIT'
]);

export const REASON_LABELS = Object.freeze({
  AWAITING_MATCH: 'Waiting for a match',
  AMBIGUOUS: 'More than one patient matches equally',
  UNREADABLE: 'The PDF could not be read: match it by hand',
  CONFLICT: 'A record disagrees with the report',
  PEOPLE_NOT_SPLIT: 'The people on the report could not be told apart'
});

// A short flag for the list's State column, its reason in full as the flag's label.
export const REASON_FLAGS = Object.freeze({
  UNREADABLE: 'Unreadable',
  CONFLICT: 'Conflict',
  PEOPLE_NOT_SPLIT: 'People not told apart'
});

// Said on the item's page for a report whose people the reader could not tell apart, in the words the
// Test says it in (backend labReportIntake.PEOPLE_NOT_SPLIT_MESSAGE): nothing of it is read into a
// Test, so whatever it is matched to, its results are entered by hand.
export const PEOPLE_NOT_SPLIT_TEXT =
  'This report gives results for more than one person, or for a pregnancy, and the reader could not tell whose each result is, so nothing in it is read into a Test. Enter the result of the person each Test is for by hand, as the report gives it.';

export const MATCH_BANDS = freeze([
  'identifier',
  'probable',
  'possible',
  'weak',
  'conflict'
]);

export const BAND_LABELS = Object.freeze({
  identifier: 'Identifier match',
  probable: 'Probable',
  possible: 'Possible',
  weak: 'Weak',
  conflict: 'Conflict'
});

// The rule each band means, as inboxMatcher.bandFor decides it.
export const BAND_DESCRIPTIONS = Object.freeze({
  identifier:
    'An identifier agrees (a checked national number of the scheme this group declared, an earlier report’s number, or a TrakGene specimen id), the name or date of birth agrees too, and nothing disagrees.',
  probable:
    'A hospital number, or a Healthcare ID that cannot count as an identifier, agrees with the surname or the full date of birth, and nothing disagrees.',
  possible:
    'The surname and the full date of birth agree, and no identifier could be compared. Name, date of birth, sex and postcode together never go higher than this.',
  weak: 'Only part of the identity agrees (the date of birth read the other way round, a maiden or given name), or something disagrees.',
  conflict:
    'An identifier disagrees, or one agrees while the date of birth or the surname disagrees. It cannot be confirmed: correct the record by the ordinary edit, or match the report to someone else.'
});

// The order a list of bands reads in, best first.
export const BAND_RANK = Object.freeze(
  Object.fromEntries(MATCH_BANDS.map((band, i) => [band, i]))
);

export const EVIDENCE_OUTCOMES = freeze([
  'agrees',
  'disagrees',
  'not-on-report',
  'not-on-record',
  'not-comparable'
]);

export const OUTCOME_LABELS = Object.freeze({
  agrees: 'Agrees',
  disagrees: 'Disagrees',
  'not-on-report': 'Not on the report',
  'not-on-record': 'Not on the record',
  'not-comparable': 'Cannot be compared'
});

// The matcher's evidence items, in the order it returns them (inboxMatcher EVIDENCE_ITEMS).
export const EVIDENCE_ITEMS = freeze([
  'national-id',
  'hospital-number',
  'report-key',
  'specimen-id',
  'surname',
  'maiden-name',
  'given-name',
  'dob',
  'sex',
  'life-status',
  'postcode'
]);

// One person of a report about two or more people (TGCLOUD-1868, Phase E3): a person's evidence,
// then whether the records agree with how the report says they are related to the others on it
// (inboxMatcher INDIVIDUAL_EVIDENCE_ITEMS).
export const INDIVIDUAL_EVIDENCE_ITEMS = freeze([
  ...EVIDENCE_ITEMS,
  'relationship'
]);

export const EVIDENCE_ITEM_LABELS = Object.freeze({
  'national-id': 'Healthcare ID',
  'hospital-number': 'Hospital number',
  'report-key': 'Earlier report number',
  'specimen-id': 'Specimen id',
  surname: 'Surname',
  'maiden-name': 'Maiden name',
  'given-name': 'Given name',
  dob: 'Date of birth',
  sex: 'Sex',
  'life-status': 'Alive at collection',
  postcode: 'Postcode',
  relationship: 'Relationship'
});

// Why an item could not be compared, or how it agreed (an evidence item's `detail`). Every detail the
// matcher gives is one of rules.EVIDENCE_DETAILS (TGCLOUD-1868, round 5: each has its own words), and
// labReportRefusalVocabulary.test.js fails on one with no words here.
export const EVIDENCE_DETAIL_LABELS = Object.freeze({
  'too-common': 'Held by more than 20 people in this group, so not used.',
  'card-only':
    'The Medicare card agrees; the individual reference number (IRN) is on only one of them, or on neither.',
  differs:
    'The numbers differ, but this field may hold a different kind of number, so it counts for nothing either way.',
  'maiden-name': 'The surname differs, and the maiden name agrees.',
  'initial-only': 'Only an initial fits.',
  estimated: 'The record’s date is estimated.',
  'partial-date': 'The record’s date is not a full date.',
  unreadable: 'The record’s date could not be read.',
  // The report prints one particular of this person two ways (TGCLOUD-1868, decision 25: its patient
  // block and their row of the samples table disagree). One of the two is wrong whatever the record
  // says, so it is a conflict, and the way past is the laboratory's corrected report.
  'printed-differently':
    'The report prints this differently in two places for this person, in its patient details and in its table of samples. One of them is wrong whatever the record says: ask the laboratory for a corrected report.',
  // How the report says its people are related, against the records (TGCLOUD-1868, Phase E3). The
  // partner check is a conflict only on a contradiction (decision 27): the pregnancy's recorded
  // other parent first, then a father matched in another family. A partner the pedigree does not
  // record yet is no contradiction.
  'not-matched-yet':
    'The other person this report names is not matched yet, so this cannot be compared.',
  partners: 'The family’s pedigree shows them as partners.',
  'not-recorded-as-partners':
    'Both are in the family’s pedigree, and it does not record them as partners. That is no contradiction: a partner may not have been added yet.',
  'pregnancy-other-parent':
    'The pregnancy the fetus on this report is matched to records this person as its other parent.',
  'pregnancy-other-parent-differs':
    'The pregnancy the fetus on this report is matched to records someone else as its other parent. Correct the pregnancy in the pedigree, or match this person to someone else.',
  'other-family':
    'They are in different families, which contradicts the report. Match this person to someone else, or correct the family, before confirming.',
  'not-in-pedigree':
    'One of them is not drawn in the family’s pedigree, so it cannot say.',
  'same-person':
    'This is the person already matched as the other parent on this report.',
  'fetus-in-her-pregnancy':
    'The fetus on this report is matched to a pregnancy of this person.',
  'fetus-in-another-pregnancy':
    'The fetus on this report is matched to a pregnancy of someone else.',
  'pregnancy-of-mother':
    'This pregnancy is recorded for the person matched as the mother on this report.',
  'pregnancy-of-another':
    'This pregnancy is recorded for someone other than the person matched as the mother on this report.',
  // A single-fetus report's pregnancy before its fetus is matched (TGCLOUD-1868 closeout review): every
  // fetus record is found under a pregnancy of the mother it would be checked against, so it is not
  // compared until one is confirmed, rather than agreeing by construction.
  'mother-through-fetus':
    'The mother on this report is found only through the fetus, so this is checked once a fetus record is confirmed.'
});

export const VERIFICATION_METHODS = freeze([
  'request-form',
  'requester',
  'laboratory',
  'sample-record',
  'other'
]);

export const VERIFICATION_METHOD_LABELS = Object.freeze({
  'request-form': 'Checked against the request form',
  requester: 'Confirmed with the requesting clinician',
  laboratory: 'Confirmed with the laboratory',
  'sample-record': 'Checked against the sample record',
  other: 'Another way (say what in the note)'
});

// Which particulars a person compared with the report (TGCLOUD-1868 B8): a weak match needs at
// least WEAK_MATCH_MIN_PARTICULARS of them as well as its method. Codes on the wire and in the log;
// these words on screen. The national id is the "Healthcare ID", as everywhere else in the inbox.
export const VERIFICATION_PARTICULARS = freeze([
  'name',
  'dob',
  'national-id',
  'hospital-number',
  'sample-id',
  'request-number'
]);

export const VERIFICATION_PARTICULAR_LABELS = Object.freeze({
  name: 'Name',
  dob: 'Date of birth',
  'national-id': 'Healthcare ID',
  'hospital-number': 'Hospital number',
  'sample-id': 'Sample ID',
  'request-number': 'Request number'
});

export const WEAK_MATCH_MIN_PARTICULARS = 2;

// The particulars as words inside a sentence ("date of birth"), each a key of its own for
// translate(): a heading's capital is not the word's.
const PARTICULAR_WORDS = Object.freeze(
  Object.fromEntries(
    Object.entries(VERIFICATION_PARTICULAR_LABELS).map(([code, label]) => [
      code,
      label.charAt(0).toLowerCase() + label.slice(1)
    ])
  )
);

/**
 * The particulars a verification names, in words: "Compared with the report: name and date of
 * birth." '' when it names none. In the vocabulary's order; a code the screen does not know is
 * shown as itself, never dropped. In the selected language: each label is translated as the word
 * inside the sentence ("date of birth"), not as the heading it is elsewhere. PURE.
 */
export function particularsText(codes) {
  const given = Array.isArray(codes) ? codes : [];
  const words = [
    ...VERIFICATION_PARTICULARS.filter((p) => given.includes(p)),
    ...given.filter((p) => !VERIFICATION_PARTICULARS.includes(p))
  ].map((p) =>
    PARTICULAR_WORDS[p] ? translate(PARTICULAR_WORDS[p]) : String(p)
  );
  if (!words.length) return '';
  const list =
    words.length === 1
      ? words[0]
      : translate('{{list}} and {{last}}', {
          list: words.slice(0, -1).join(', '),
          last: words[words.length - 1]
        });
  return translate('Compared with the report: {{list}}.', { list });
}

export const INBOX_REJECT_REASONS = freeze([
  'dob-differs',
  'name-differs',
  'identifier-differs',
  'sex-differs',
  'not-this-patient',
  'other'
]);

export const REJECT_REASON_LABELS = Object.freeze({
  'dob-differs': 'The date of birth differs',
  'name-differs': 'The name differs',
  'identifier-differs': 'An identifier differs',
  'sex-differs': 'The sex differs',
  'not-this-patient': 'Not this patient',
  other: 'Another reason (say what in the note)'
});

export const INBOX_DISMISS_REASONS = freeze([
  'not-a-lab-report',
  'duplicate',
  'not-our-patient',
  'other'
]);

export const DISMISS_REASON_LABELS = Object.freeze({
  'not-a-lab-report': 'Not a lab report',
  duplicate: 'A duplicate of another report',
  'not-our-patient': 'Not our patient (another organisation’s report)',
  other: 'Another reason (say what in the note)'
});

export const INBOX_DECISION_ACTIONS = freeze([
  'confirmed',
  'rejected',
  'unrejected',
  'match-undone',
  'dismissed',
  'reopened',
  'handed-off',
  'linked',
  'returned-wrong-patient',
  'returned-wrong-test',
  'released-test-deleted',
  'received-again',
  // One person of a report about several (TGCLOUD-1868, decision 28).
  'set-aside',
  'set-aside-undone'
]);

export const DECISION_LABELS = Object.freeze({
  confirmed: 'Match confirmed',
  rejected: 'Candidate rejected',
  unrejected: 'Rejection undone',
  'match-undone': 'Match undone',
  dismissed: 'Dismissed',
  reopened: 'Reopened',
  'handed-off': 'Read into a Test',
  linked: 'Applied to a Test',
  'returned-wrong-patient': 'Returned: the wrong patient',
  'returned-wrong-test': 'Returned: the wrong Test',
  'released-test-deleted': 'Returned: the Test was deleted',
  'received-again': 'Received again',
  'set-aside': 'Set aside',
  'set-aside-undone': 'Set-aside undone'
});

// ── One person set aside (TGCLOUD-1868, decision 28) ──────────────────────────────────────────
//
// A person on a report about several who is not this service's patient (the father of a fetus,
// say) is set aside on their own, with a reason from this list and no default; 'other' needs a note,
// and a note naming someone is refused, as every inbox note is. It is undone the same way. The report
// completes once everyone on it is linked or set aside; with everyone set aside it is dismissed.
export const INBOX_SET_ASIDE_REASONS = freeze(['not-our-patient', 'other']);

export const SET_ASIDE_REASON_LABELS = Object.freeze({
  'not-our-patient': 'Not our patient',
  other: 'Another reason (say what in the note)'
});

// The whole report, once someone on it is matched or applied to a Test: it is not dismissed (the
// server refuses with 409 INBOX_DISMISS_AFTER_MATCH), and everyone else is set aside on their own.
export const DISMISS_AFTER_MATCH_TEXT =
  'Someone on this report is matched or applied to a Test, so the whole report cannot be dismissed. Set aside, one at a time, anyone on it who is not our patient.';
// ...said when the server refuses it, the state having moved since the page was read.
export const DISMISS_REFUSED_AFTER_MATCH =
  'Someone on this report has been matched or applied to a Test since this page was read, so the whole report cannot be dismissed. Set aside, one at a time, anyone on it who is not our patient.';
// A report whose people are all set aside has left the inbox: undone one at a time, never reopened.
export const ALL_SET_ASIDE_TEXT =
  'Everyone on this report is set aside, so it has left the inbox. Undo a set-aside below to match that person here again.';

// UNDOING A SET-ASIDE REOPENS THE PERSON, AND THE REPORT WITH THEM (TGCLOUD-1868, round 5). The
// report's state is its people's, so one that left the inbox because everyone else on it is applied
// to a Test, or because everyone on it is set aside, comes back to the inbox in the same step. Said
// beside the undo on a report the others' Tests completed (one whose people are all set aside says
// it in its Dismissed panel), and in the undo's dialog on either.
export const UNDO_SET_ASIDE_REOPENS_TEXT =
  'This report has left the Lab Report Inbox. Undoing this set-aside brings it back, with this person open to be matched.';

// ── A single-fetus report (TGCLOUD-1868, decision 32) ──────────────────────────────────────────────
//
// Its samples show one fetus and its results are one patient's, the fetus's. Its inbox item has ONE
// individual, the fetus (key and role `fetus`: backend inboxIndividuals.isSingleFetusItem), matched to
// a fetus record of a pregnancy of the mother its samples table prints, as a trio's fetus is. The
// mother is no individual of it: she is matched only through the fetus. So what a report about
// several people says ("everyone else on the report", "covers 3 individuals") is said here for the
// one fetus.
export const SINGLE_FETUS_HEADING = 'Which fetus is this report about?';
export const SINGLE_FETUS_INTRO =
  'This report gives the result of one fetus. It is matched to a fetus record of a pregnancy of its mother, on what the report prints about her, and its result goes to a Test for that fetus on her record.';
// "What the report says", where a report about one person shows its patient as printed. What the
// samples table prints for the mother is shown with the fetus (the server's `motherIdentity`;
// TGCLOUD-1868 closeout review: it was in each candidate's evidence alone, and so nowhere with none).
export const SINGLE_FETUS_PRINTED_TEXT =
  'This report gives one fetus’s result. The fetus is matched through its mother: what the report prints about her is shown with the fetus below.';
export const MOTHER_AS_PRINTED_HEADING = 'The mother, as printed';
// Once the fetus is matched the report is not dismissed (the server refuses with 409
// INBOX_DISMISS_AFTER_MATCH): its match is undone first. Linked, it has left the inbox.
export const SINGLE_FETUS_DISMISS_AFTER_MATCH_TEXT =
  'The fetus on this report is matched, so the report is not dismissed. To dismiss it, undo the match first.';
// ...said when the server refuses it, the fetus having been matched since the page was read.
export const SINGLE_FETUS_DISMISS_REFUSED =
  'The fetus on this report has been matched since this page was read, so the report is not dismissed. To dismiss it, undo the match first.';
// The fetus set aside as not this service's patient: the report has left the inbox that way.
export const SINGLE_FETUS_SET_ASIDE_TEXT =
  'The fetus on this report is set aside, so the report has left the inbox. Undo the set-aside below to match the fetus here again.';
// THE MOTHER TIES (TGCLOUD-1868 closeout review). Her samples row finds two people in the group
// equally (a duplicate or a stub of hers: the same name and date of birth), so no fetus record of
// either can be confirmed (decision 26: the system never picks). She is no individual of the report,
// so the people who tie as her are listed with the fetus (the server's `mothers`), and whoever is not
// her is rejected AS THE MOTHER (`asMother`), never confirmed: the fetus is matched to a fetus record.
export const SINGLE_FETUS_MOTHERS_TIED_TEXT =
  'More than one person in this group matches the mother on this report equally, and nothing on it tells them apart, so no fetus record can be confirmed. Reject each one who is not the mother, saying why. The system never chooses.';
export const MOTHERS_TIED_HEADING = 'Who matches the mother on this report';
export const NOT_THE_MOTHER_LABEL = 'Not the mother';

/**
 * Whether a person who ties as the mother has a fetus record listed with the fetus, in the selected
 * language. PURE.
 */
export function motherFetusRecordsText(count) {
  const n = Number(count);
  if (!Number.isInteger(n) || n <= 0) {
    return translate(
      'No ongoing pregnancy with a fetus record is recorded for her, so nothing of hers is listed below.'
    );
  }
  return n === 1
    ? translate('One ongoing fetus record of hers is listed below.')
    : translate('{{count}} ongoing fetus records of hers are listed below.', {
        count: n
      });
}

// Why a candidate cannot be confirmed (a candidate's `notConfirmableBecause`).
export const NOT_CONFIRMABLE_LABELS = Object.freeze({
  rejected: 'Rejected for this report.',
  conflict:
    'The report and this record disagree on an identifier, or on the date of birth or surname beside an identifier that agrees. Correct the record by the ordinary edit, or match the report to someone else.',
  ambiguous:
    'More than one person matches this report equally and nothing on it tells them apart. Reject the wrong one first, saying why.'
});

// ── A report about two or more people (TGCLOUD-1868, Phase E2 and E3) ───────────────────────────
//
// A trio's or a prenatal report gives a result for each person its tables name. Its inbox item has
// `individuals`, each matched, rejected and undone on its own; the codes below are rules.js's.

// Who an individual is on the report (rules.INDIVIDUAL_ROLES): a code read from the role cell its
// row opens with, never from a name.
export const INDIVIDUAL_ROLES = freeze([
  'proband',
  'fetus',
  'mother',
  'father',
  'sibling',
  'child',
  'twin',
  'baby',
  'partner',
  'other'
]);

// A twin or a baby the report does not say is a fetus or a born child (rules.UNDETERMINED_ROLES): one
// whose samples row says it is a fetus's is read as a fetus, and any other fits no Test and is
// matched to nobody (TGCLOUD-1868 review).
export const UNDETERMINED_ROLES = freeze(['twin', 'baby']);

export const INDIVIDUAL_ROLE_LABELS = Object.freeze({
  proband: 'Proband',
  fetus: 'Fetus',
  mother: 'Mother',
  father: 'Father',
  sibling: 'Sibling',
  child: 'Child',
  twin: 'Twin',
  baby: 'Baby',
  partner: 'Partner',
  other: 'Another person'
});

// Said for a twin or a baby the report does not say is a fetus or a born child.
export const UNDETERMINED_ROLE_TEXT =
  'The report does not say whether this twin or baby is a fetus or a born child, so they are not matched here and their result is not read into any Test. Enter it by hand, as the report gives it, on the Test it belongs to.';

// An individual's own state (rules.INBOX_INDIVIDUAL_STATES): the item's is their aggregate, and a
// dismissal is the whole item's, never one individual's. 'set-aside' is one person's alone
// (TGCLOUD-1868, decision 28): every person linked or set aside, one at least linked, is a linked
// report; everyone set aside, a dismissed one.
export const INBOX_INDIVIDUAL_STATES = freeze([
  'open',
  'matched',
  'linked',
  'set-aside'
]);

// Why a fetus has no candidates (rules.FETUS_WAITING_REASONS). A fetus is matched to a fetus record
// of a pregnancy of the report's mother, so it waits on the report naming her, on her being found,
// and on the pedigree recording the pregnancy and the fetus. Nothing is written to the pedigree from
// here: each says where to add what is missing.
export const FETUS_WAITING_REASONS = freeze([
  'no-mother-on-report',
  'mother-not-found',
  'no-pregnancy-recorded',
  'no-fetus-recorded'
]);

export const FETUS_WAITING_LABELS = Object.freeze({
  'no-mother-on-report':
    'The report names no mother for this fetus, so there is no pregnancy to match it to here.',
  'mother-not-found':
    'Nobody in this group matches the mother on this report, so there is no pregnancy to look in. Match the mother first, then the fetus here.',
  // The plan's own words (TGCLOUD-1868, Phase E3), with "ongoing" since decision 26: a fetus is
  // matched only to a fetus record of a pregnancy with no outcome recorded, and never to a hidden one.
  // Said alone, they told staff to add a pregnancy where the one the report is about has ENDED, which
  // invites a false pregnancy in the pedigree (TGCLOUD-1868 review), so they say what to do then too.
  'no-pregnancy-recorded':
    'No ongoing pregnancy is recorded for this person. If the pregnancy is missing from the pedigree, add it there, then match the fetus here. A pregnancy that has ended is not matched here: enter its result by hand on a Test for the fetus.',
  'no-fetus-recorded':
    'The ongoing pregnancy recorded for this person has no fetus record drawn in the pedigree. Add the fetus in the pedigree, then match it here.'
});

// The same, for a SINGLE-FETUS report's fetus (decision 32; TGCLOUD-1868 closeout review). Its mother
// is no person of the report, so there is no mother to match first, no search and no family to make
// on the page: each says only what can be done, and where. With no mother row the fetus is never
// matched here, and the way to read its result into the fetus's Test is said: dismissed here, the
// report is added on that Test, where decision 33's acknowledgement is asked.
export const SINGLE_FETUS_WAITING_LABELS = Object.freeze({
  'no-mother-on-report':
    'The report prints no row for the fetus’s mother in its table of samples, so nothing here can find her pregnancy, and the fetus cannot be matched in the inbox. To read its result into a Test for the fetus, dismiss the report here, saying why, then add it on that Test, where you confirm it is her pregnancy’s report.',
  'mother-not-found':
    'Nobody in this group matches the mother this report prints for the fetus, so there is no pregnancy of hers to look in. If she is in this group, correct her record by the ordinary edit; if not, add her, her pregnancy and the fetus in the pedigree. The fetus is found here next time.',
  'no-pregnancy-recorded':
    'No ongoing pregnancy is recorded for the mother this report prints. If her pregnancy is missing from the pedigree, add it there with the fetus, then match the fetus here. A pregnancy that has ended is not matched here: enter its result by hand on a Test for the fetus.',
  'no-fetus-recorded':
    'The ongoing pregnancy recorded for the mother this report prints has no fetus record drawn in the pedigree. Add the fetus in the pedigree, then match it here.'
});

// A conflict that is the REPORT's own (TGCLOUD-1868, decision 25: it prints this person two ways,
// `printed-differently`): no edit of the record or the pedigree ends it, so the conflict's usual words
// would send the reader to the wrong place.
export const PRINTED_DIFFERENTLY_CONFLICT_TEXT =
  'The report prints this person differently in two places (see the evidence), so nobody can be confirmed for them on it. Ask the laboratory for a corrected report.';

// Why one individual's candidate cannot be confirmed. A conflict may come from how the report says
// its people are related (the Relationship evidence) as well as from an identifier, so the words
// for a single patient's would point at the wrong thing.
export const INDIVIDUAL_NOT_CONFIRMABLE_LABELS = Object.freeze({
  rejected: 'Rejected for this person on the report.',
  conflict:
    'This record disagrees with the report, on an identifier or on how the report says its people are related (see the evidence). Correct the record or the pedigree by the ordinary edit, or match this person to someone else.',
  ambiguous:
    'More than one record fits this person on the report equally, and nothing on it tells them apart. Reject the wrong one first, saying why.'
});

// The relationships a report states for one of its people (inboxIndividuals.statedRelationships),
// as the Relationship evidence says them: what the report says, on its side.
export const RELATIONSHIP_RELATIONS = freeze([
  'partner',
  'pregnancy',
  'carrier'
]);

export const RELATIONSHIP_STATED_LABELS = Object.freeze({
  partner: 'The other parent’s partner',
  pregnancy: 'In the mother’s pregnancy',
  carrier: 'The fetus’s mother'
});

// What the records say, on their side, for each relationship check (inboxMatcher.relationshipCheck's
// `detail`). Short, beside the report's side; the evidence note under the outcome says the rest.
// Nothing is said while the other person is not matched: there is nothing to compare yet.
export const RELATIONSHIP_RECORD_LABELS = Object.freeze({
  'not-matched-yet': '',
  partners: 'Partners in the pedigree',
  'not-recorded-as-partners': 'Not recorded as partners in the pedigree',
  'pregnancy-other-parent': 'The other parent of the fetus’s pregnancy',
  'pregnancy-other-parent-differs':
    'Someone else is the other parent of the fetus’s pregnancy',
  'other-family': 'In different families',
  'not-in-pedigree': 'Not both drawn in the pedigree',
  'same-person': 'The same person as the other parent',
  'fetus-in-her-pregnancy': 'The fetus is in her pregnancy',
  'fetus-in-another-pregnancy': 'The fetus is in someone else’s pregnancy',
  'pregnancy-of-mother': 'A pregnancy of the matched mother',
  'pregnancy-of-another': 'A pregnancy of someone else',
  // Nothing to compare yet, as for 'not-matched-yet': the note says why.
  'mother-through-fetus': ''
});

// A person the matcher's searches found but did not propose, listed because nothing on the report
// tells them apart from a candidate or the patient picked (a candidate's `foundNotProposed`).
export const FOUND_NOT_PROPOSED_LABEL = 'Found, not proposed';
export const FOUND_NOT_PROPOSED_DESCRIPTION =
  'Found by the date of birth or the surname, but not proposed: the report does not match them well enough. Nothing on it tells them apart from a patient who is, so reject them first if they are not this patient.';
export const PICKED_LABEL = 'Picked by search';
// Why the page cannot confirm the patient picked by search (`chosen`, ambiguous).
export const PICK_TIED_MESSAGE =
  'Someone else matches this report as well as the patient you picked, and nothing on it tells them apart, so the patient you picked cannot be confirmed. They are shown below. Reject them first, saying why. The system never chooses.';

// How a matched person's Test is ranked for the report (inboxMatcher.rankTestsForReport).
export const TEST_REASONS = freeze([
  'amends',
  'same-code',
  'name-or-lab',
  'other'
]);

export const TEST_REASON_LABELS = Object.freeze({
  amends: 'This report amends a report already on this Test',
  'same-code': 'Not yet resulted, with the same test code',
  'name-or-lab': 'Not yet resulted, with the same test name or laboratory',
  other: 'Another of their Tests'
});

export const HEALTHCARE_ID_SCHEMES = freeze([
  'nhs',
  'chi',
  'ihi',
  'medicare',
  'other'
]);

export const HEALTHCARE_ID_SCHEME_LABELS = Object.freeze({
  nhs: 'NHS number',
  chi: 'CHI number',
  ihi: 'IHI (Individual Healthcare Identifier)',
  medicare: 'Medicare number',
  other: 'Another kind of number',
  unknown: 'A number of an unnamed scheme'
});

// How the reader read a report's conclusion (rules.CONCLUSIONS; an item's `summary.conclusion`).
// Said as a reading, never as the result: the report's own words are on the report, and the Test
// result is the clinician's choice (TGCLOUD-1868, A6: the row printed the code).
export const CONCLUSIONS = freeze([
  'positive',
  'negative',
  'inconclusive',
  'not-stated'
]);

export const CONCLUSION_LABELS = Object.freeze({
  positive: 'Read as: positive',
  negative: 'Read as: negative',
  inconclusive: 'Read as: inconclusive',
  'not-stated': 'Read as: not stated'
});

// The report's own numbers (testresultidentifiers report keys).
export const REPORT_KEY_LABELS = Object.freeze({
  'lab-report-id': 'Lab report number',
  accession: 'Accession number',
  requisition: 'Requisition number',
  other: 'Other report number'
});

/**
 * A code's label in the selected language, or the code itself when the screen
 * does not know it (never blank). Every label here is this screen's own words,
 * so it is translated (TGCLOUD-1893); an unknown code is shown as it is.
 */
export const labelOf = (labels, code) =>
  code == null || code === ''
    ? ''
    : labels[code]
    ? translate(labels[code])
    : String(code);

/**
 * A code's label as a word inside a sentence ("the same accession number"): the
 * English in lower case, THEN translated, so a language that capitalises its
 * nouns keeps them (lower-casing a translation broke German). A code the screen
 * does not know is shown as itself, in lower case.
 */
export const wordOf = (labels, code) =>
  code == null || code === ''
    ? ''
    : labels[code]
    ? translate(labels[code].toLowerCase())
    : String(code).toLowerCase();
